import { useState, useEffect } from 'react';
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COLLECTIONS, ENV } from '../collections';
import { buildChainLookup, sameChain } from '../utils/hierarchy';

// Firestore rejects batches over 500 writes.
const BATCH_SIZE = 450;

async function commitInChunks(items, apply) {
  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    items.slice(i, i + BATCH_SIZE).forEach((item) => apply(batch, item));
    await batch.commit();
  }
}

/* ───────────────── Cycles ───────────────── */

export function useCycles() {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();

  useEffect(() => {
    const q = isAdmin
      ? query(collection(db, COLLECTIONS.cycles), orderBy('createdAt', 'desc'))
      : query(collection(db, COLLECTIONS.cycles), where('status', 'in', ['active', 'completed']), orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(q, (snap) => {
      setCycles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { console.error('Cycles listener error', err); setLoading(false); });
    return unsub;
  }, [isAdmin]);

  return { cycles, loading };
}

export async function createCycle(data) {
  return addDoc(collection(db, COLLECTIONS.cycles), {
    name: data.name,
    description: data.description || '',
    effectiveDate: data.effectiveDate || '',
    status: 'draft',
    compCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateCycle(cycleId, data) {
  return updateDoc(doc(db, COLLECTIONS.cycles, cycleId), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteCycle(cycleId) {
  const [compsSnap, linesSnap] = await Promise.all([
    getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations)),
    getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.reportingLines)),
  ]);
  await commitInChunks([...compsSnap.docs, ...linesSnap.docs], (batch, d) => batch.delete(d.ref));
  return deleteDoc(doc(db, COLLECTIONS.cycles, cycleId));
}

/* ──────────── Compensations (admin – per cycle) ──────────── */

export function useCompensations(cycleId, refreshDep = 0) {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cycleId) { setLoading(false); return; }
    const q = query(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations), orderBy('employeeName'));
    const unsub = onSnapshot(q, (snap) => {
      setComps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { console.error('Compensations listener error', err); setLoading(false); });
    return unsub;
  }, [cycleId, refreshDep]);

  return { comps, loading };
}

export async function saveCompensation(cycleId, compId, data) {
  const clean = { ...data, env: ENV, updatedAt: serverTimestamp() };
  if (compId) {
    await updateDoc(doc(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations, compId), clean);
  } else {
    clean.createdAt = serverTimestamp();
    await addDoc(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations), clean);
  }
  await stampSupervisorChains(cycleId);
}

export async function deleteCompensation(cycleId, compId) {
  return deleteDoc(doc(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations, compId));
}

/**
 * Bulk-import compensations from a parsed CSV array.
 * Each row is matched by employeeEmail — existing docs are updated, new ones created.
 * Returns { created, updated, errors }.
 */
export async function bulkImportCompensations(cycleId, rows) {
  // Fetch current compensations to detect updates vs creates
  const existing = await getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations));
  const byEmail = {};
  existing.docs.forEach((d) => {
    const data = d.data();
    if (data.employeeEmail) byEmail[data.employeeEmail.toLowerCase()] = d.id;
  });

  let created = 0;
  let updated = 0;
  const errors = [];

  for (let i = 0; i < rows.length; i += BATCH_SIZE) {
    const chunk = rows.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    for (const row of chunk) {
      try {
        const clean = { ...row, env: ENV, updatedAt: serverTimestamp() };
        const key = row.employeeEmail?.toLowerCase();
        if (key && byEmail[key]) {
          // Update existing
          batch.update(doc(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations, byEmail[key]), clean);
          updated++;
        } else {
          // Create new
          const newRef = doc(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations));
          batch.set(newRef, { ...clean, createdAt: serverTimestamp() });
          created++;
        }
      } catch (e) {
        errors.push({ row: row.employeeName || row.employeeEmail || '(unknown)', error: e.message });
      }
    }

    await batch.commit();
  }

  // Update cycle comp count
  const totalSnap = await getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations));
  await updateDoc(doc(db, COLLECTIONS.cycles, cycleId), { compCount: totalSnap.size, updatedAt: serverTimestamp() });
  await stampSupervisorChains(cycleId);

  return { created, updated, errors };
}

/* ──── Unique communicators (for admin impersonation) ──── */
// Merges two sources:
//  1. userRoles docs where isCommunicator == true  (always available)
//  2. Compensation docs with communicatorEmail set  (may include names)
// The compensation source enriches names; the userRoles source ensures
// communicators appear even before any compensations are assigned.

export function useCommunicators(enabled = true) {
  const [communicators, setCommunicators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) { setCommunicators([]); setLoading(false); return; }
    const map = {};
    let gotRoles = false;
    let gotComps = false;

    const emit = () => {
      if (!gotRoles || !gotComps) return;
      setCommunicators(
        Object.values(map).sort((a, b) => a.name.localeCompare(b.name)),
      );
      setLoading(false);
    };

    // Source 1: userRoles with isCommunicator == true
    const rolesQ = query(
      collection(db, COLLECTIONS.userRoles),
      where('isCommunicator', '==', true),
    );
    const unsubRoles = onSnapshot(rolesQ, (snap) => {
      // Reset role-sourced entries, keep comp-sourced ones
      snap.docs.forEach((d) => {
        const email = d.id;
        if (!map[email]) {
          map[email] = { email, name: email.split('@')[0] };
        }
      });
      gotRoles = true;
      emit();
    }, (err) => { console.error('Communicator roles listener error', err); gotRoles = true; emit(); });

    // Source 2: compensations with communicatorEmail (enriches with names)
    const compsQ = query(
      collectionGroup(db, COLLECTIONS.compensations),
      where('env', '==', ENV),
      where('communicatorEmail', '!=', null),
    );
    const unsubComps = onSnapshot(compsQ, (snap) => {
      snap.docs
        .filter((d) => d.ref.parent.parent.parent.id === COLLECTIONS.cycles)
        .forEach((d) => {
          const data = d.data();
          const email = data.communicatorEmail;
          if (email) {
            // Compensation source wins for the name since it may have the full name
            map[email] = {
              email,
              name: data.communicatorName || map[email]?.name || email.split('@')[0],
            };
          }
        });
      gotComps = true;
      emit();
    }, (err) => { console.error('Communicator comps listener error', err); gotComps = true; emit(); });

    return () => { unsubRoles(); unsubComps(); };
  }, [enabled]);

  return { communicators, loading };
}

/* ──── Communicator compensations (collection-group) ──── */

export function useCommunicatorComps(email) {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) { setLoading(false); return; }
    const q = query(collectionGroup(db, COLLECTIONS.compensations), where('env', '==', ENV), where('communicatorEmail', '==', email));
    const unsub = onSnapshot(q, (snap) => {
      setComps(
        snap.docs
          .filter((d) => d.ref.parent.parent.parent.id === COLLECTIONS.cycles)
          .map((d) => ({
            id: d.id,
            cycleId: d.ref.parent.parent.id,
            ...d.data(),
          })),
      );
      setLoading(false);
    }, (err) => { console.error('Communicator comps listener error', err); setLoading(false); });
    return unsub;
  }, [email]);

  return { comps, loading };
}

/* ──── Team view: subordinate communicators' compensations ──── */

/**
 * For a given communicator email, finds all compensation docs where this
 * user appears in the `supervisorChain` array. This means the comp's
 * communicator reports (directly or transitively) to this user.
 *
 * Returns { teamComps, loading } across every cycle; callers scope by cycle.
 */
export function useTeamComps(communicatorEmail) {
  const [teamComps, setTeamComps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!communicatorEmail) { setLoading(false); return; }
    const q = query(
      collectionGroup(db, COLLECTIONS.compensations),
      where('env', '==', ENV),
      where('supervisorChain', 'array-contains', communicatorEmail.toLowerCase()),
    );
    const unsub = onSnapshot(q, (snap) => {
      setTeamComps(
        snap.docs
          .filter((d) => d.ref.parent.parent.parent.id === COLLECTIONS.cycles)
          .map((d) => ({ id: d.id, cycleId: d.ref.parent.parent.id, ...d.data() })),
      );
      setLoading(false);
    }, (err) => { console.error('Team comps listener error', err); setLoading(false); });
    return unsub;
  }, [communicatorEmail]);

  return { teamComps, loading };
}

/* ──── Reporting Lines (per cycle) ──── */

export function useReportingLines(cycleId, refreshDep = 0) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cycleId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.reportingLines),
      orderBy('employeeName'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setLines(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { console.error('ReportingLines listener error', err); setLoading(false); });
    return unsub;
  }, [cycleId, refreshDep]);

  return { lines, loading };
}

/**
 * Bulk-import reporting lines from a parsed CSV array.
 * Replaces all existing reporting lines for the cycle.
 * Returns { imported, errors }.
 */
export async function bulkImportReportingLines(cycleId, rows) {
  const existing = await getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.reportingLines));
  await commitInChunks(existing.docs, (batch, d) => batch.delete(d.ref));

  let imported = 0;
  const errors = [];
  const linesCol = collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.reportingLines);
  await commitInChunks(rows, (batch, row) => {
    try {
      batch.set(doc(linesCol), {
        employeeEmail: row.employeeEmail,
        employeeName: row.employeeName ?? '',
        supervisorEmail: row.supervisorEmail,
        supervisorName: row.supervisorName ?? '',
        env: ENV,
        createdAt: serverTimestamp(),
      });
      imported++;
    } catch (e) {
      errors.push({ row: row.employeeName || row.employeeEmail || '(unknown)', error: e.message });
    }
  });

  // Update cycle with reporting line count
  await updateDoc(doc(db, COLLECTIONS.cycles, cycleId), {
    reportingLineCount: imported,
    updatedAt: serverTimestamp(),
  });

  // Stamp supervisorChain on all compensation docs in this cycle
  await stampSupervisorChains(cycleId);

  return { imported, errors };
}

/**
 * Build supervisor chains from reporting lines and stamp them onto comp docs.
 * For each compensation, walks up the reporting hierarchy from the comp's
 * communicatorEmail and stores the full chain of supervisor emails.
 * This enables secure Firestore rules: a user can read a comp if their
 * email appears in the comp's supervisorChain.
 */
export async function stampSupervisorChains(cycleId) {
  const [linesSnap, compsSnap] = await Promise.all([
    getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.reportingLines)),
    getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations)),
  ]);
  const chainOf = buildChainLookup(linesSnap.docs.map((d) => d.data()));
  const stale = compsSnap.docs.filter((d) => !sameChain(d.data().supervisorChain, chainOf(d.data().communicatorEmail)));
  await commitInChunks(stale, (batch, d) => batch.update(d.ref, { supervisorChain: chainOf(d.data().communicatorEmail) }));
  return stale.length;
}

export async function deleteAllReportingLines(cycleId) {
  const existing = await getDocs(collection(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.reportingLines));
  await commitInChunks(existing.docs, (batch, d) => batch.delete(d.ref));
  await updateDoc(doc(db, COLLECTIONS.cycles, cycleId), {
    reportingLineCount: 0,
    updatedAt: serverTimestamp(),
  });
  await stampSupervisorChains(cycleId);
}

/* ──── Cycle lookup helper ──── */

export function useCycle(cycleId) {
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cycleId) { setLoading(false); return; }
    const unsub = onSnapshot(doc(db, COLLECTIONS.cycles, cycleId), (snap) => {
      setCycle(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    }, (err) => { console.error('Cycle listener error', err); setLoading(false); });
    return unsub;
  }, [cycleId]);

  return { cycle, loading };
}

/* ──── User Roles Management (admin-only) ──── */

/**
 * Real-time listener for all user-role documents in the current environment.
 * Returns an array of { email, isAdmin, isCommunicator }.
 */
export function useUserRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, COLLECTIONS.userRoles),
      (snap) => {
        setRoles(
          snap.docs.map((d) => ({ email: d.id, ...d.data() }))
            .sort((a, b) => a.email.localeCompare(b.email)),
        );
        setLoading(false);
      },
      (err) => { console.error('UserRoles listener error', err); setLoading(false); },
    );
    return unsub;
  }, []);

  return { roles, loading };
}

/**
 * Create or update a user-role document.
 * Shape enforced by Firestore rules: { email, isAdmin, isCommunicator, updatedAt }.
 */
export async function setUserRole(email, { isAdmin, isCommunicator, isPeoplePartner, ppAssignedEmails }) {
  const data = {
    email,
    isAdmin: !!isAdmin,
    isCommunicator: !!isCommunicator,
    isPeoplePartner: !!isPeoplePartner,
    ppAssignedEmails: isPeoplePartner ? (ppAssignedEmails || []) : [],
    updatedAt: serverTimestamp(),
  };
  return setDoc(doc(db, COLLECTIONS.userRoles, email), data);
}

/**
 * Remove a user-role document entirely.
 */
export async function deleteUserRole(email) {
  return deleteDoc(doc(db, COLLECTIONS.userRoles, email));
}

/**
 * Toggle the _presented flag on a compensation document.
 * Communicators can mark their assigned comps as communicated.
 */
export async function markCompPresented(cycleId, compId, presented) {
  return updateDoc(
    doc(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations, compId),
    { _presented: !!presented },
  );
}
